// [ START OF MODULE - VER 1.1 (src/utils/ShadowDraftService.js) ]

/**
 * TIGER EYE COMMAND CENTER - SHADOW DRAFT ENGINE
 * Provides offline resilience for field specialists executing Work Orders.
 */

const DRAFT_PREFIX = 'tecc_draft_';

export const ShadowDraftService = {
  
  /**
   * Saves the current state of a Work Order execution to local storage.
   * @param {string} workOrderId - The UUID of the Work Order.
   * @param {object} payload - The execution data (tasks, photos, signatures, status).
   * @returns {boolean} - True if successful, false if storage quota exceeded.
   */
  saveDraft: (workOrderId, payload) => {
    if (!workOrderId) return false;
    
    const draftKey = `${DRAFT_PREFIX}${workOrderId}`;
    const draftData = {
      timestamp: new Date().toISOString(),
      payload: payload
    };

    try {
      localStorage.setItem(draftKey, JSON.stringify(draftData));
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
        console.error('[TECC Shadow Draft] CRITICAL: Local storage quota exceeded. Please clear space.');
        alert('Device storage is full. Unable to save offline draft. Please sync immediately or free up space.');
      } else {
        console.error('[TECC Shadow Draft] Failed to save draft:', error);
      }
      return false;
    }
  },

  /**
   * Retrieves a saved draft for a specific Work Order.
   * @param {string} workOrderId - The UUID of the Work Order.
   * @returns {object|null} - The parsed draft data or null if not found.
   */
  loadDraft: (workOrderId) => {
    if (!workOrderId) return null;
    
    const draftKey = `${DRAFT_PREFIX}${workOrderId}`;
    const storedData = localStorage.getItem(draftKey);
    
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        return parsed.payload;
      } catch (error) {
        console.error('[TECC Shadow Draft] Failed to parse draft data:', error);
        return null;
      }
    }
    return null;
  },

  /**
   * Scans local storage and returns an array of all pending TECC drafts.
   * Useful for building an "Offline Sync Queue" UI for the technician.
   * @returns {Array} - Array of objects containing workOrderId and timestamp.
   */
  getPendingSyncs: () => {
    const pendingDrafts = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      
      if (key && key.startsWith(DRAFT_PREFIX)) {
        try {
          const storedData = localStorage.getItem(key);
          const parsed = JSON.parse(storedData);
          const workOrderId = key.replace(DRAFT_PREFIX, '');
          
          pendingDrafts.push({
            workOrderId: workOrderId,
            timestamp: parsed.timestamp,
            // Optionally include summary info here (e.g., task count) to show in the UI
          });
        } catch (error) {
          console.error(`[TECC Shadow Draft] Error reading draft ${key}:`, error);
        }
      }
    }
    
    // Sort oldest to newest
    return pendingDrafts.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  },

  /**
   * Removes a draft from local storage. Must be called AFTER a successful Supabase sync.
   * @param {string} workOrderId - The UUID of the Work Order.
   */
  clearDraft: (workOrderId) => {
    if (!workOrderId) return;
    
    const draftKey = `${DRAFT_PREFIX}${workOrderId}`;
    localStorage.removeItem(draftKey);
  },
  
  /**
   * Utility to check if a specific draft exists.
   * @param {string} workOrderId - The UUID of the Work Order.
   * @returns {boolean}
   */
  hasDraft: (workOrderId) => {
    if (!workOrderId) return false;
    return localStorage.getItem(`${DRAFT_PREFIX}${workOrderId}`) !== null;
  }
};

// [ END OF MODULE - VER 1.1 (src/utils/ShadowDraftService.js) ]